import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';

import SideControl from 'components/SideControls/SideControl';
import { GLYPHS } from 'components/Shared/Icon';
import { BREAKPOINTS, SCAFFOLDING, TRANSITION } from 'theme';
import { PANELS, changePanel, closePanel, getActivePanel } from 'modules/navigation/activePanel';
import { toggleFullscreen } from 'modules/navigation/fullscreen';
import { showModal } from 'modules/modal';
import MODAL from 'components/Shared/Modals';
import { currentUserAuthenticatedSelector } from 'modules/currentUser';
import { useRecoilState } from 'recoil';
import { currentMapIdState } from 'modules/map';

const StyledSideToolbar = styled.div`
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 0;
    right: auto;
    bottom: 0;
    left: ${SCAFFOLDING.COLUMN_WIDTH(5)};
    z-index: 1;
    box-shadow: ${SCAFFOLDING.BOX_SHADOW.BASE};
    visibility: visible;
    transform: translate(-125%, 0);
    transition: all ${TRANSITION.LENGTH_LONG} ${TRANSITION.BEZIER};

    @media (min-width: ${`${BREAKPOINTS.lg}px`}) {
        left: ${SCAFFOLDING.COLUMN_WIDTH(4)};
    }

    @media (max-width: ${`${BREAKPOINTS.md - 1}px`}) {
        display: none;
    }

    ${ifProp(
        'active',
        css`
            transform: translate(0, 0) !important;
        `
    )}

    ${ifProp(
        'fullscreen',
        css`
            left: 0 !important;
        `
    )}
`;

const propTypes = {
    activePanel: PropTypes.string,
    isAuthenticated: PropTypes.bool.isRequired,
    hasApiKey: PropTypes.bool.isRequired,
    onPanelClose: PropTypes.func.isRequired,
    onPanelOpen: PropTypes.func.isRequired,
};

const SideToolbar = ({ activePanel = null, ...props }) => {
    const {
        onMapDelete,
        onMapReset,
        fullscreen,
        onFullScreenToggle,
        onPanelOpen,
        onPanelClose,
        isAuthenticated,
        hasApiKey,
    } = props;
    const [mapId] = useRecoilState(currentMapIdState);
    const mapSaved = !!mapId;

    const isPanelActive = (panel) => activePanel === panel;
    const togglePanel = (panel) => {
        if (isPanelActive(panel)) {
            onPanelClose();
        } else {
            onPanelOpen(panel, isAuthenticated, mapSaved, hasApiKey);
        }
    };

    return (
        <StyledSideToolbar active fullscreen={fullscreen}>
            <SideControl
                title="Save"
                glyph={GLYPHS.SAVE}
                active={isPanelActive(PANELS.SAVE)}
                onClick={() => togglePanel(PANELS.SAVE)}
            />
            <SideControl
                title="View Code"
                glyph={GLYPHS.CODE}
                active={isPanelActive(PANELS.EXPORT)}
                onClick={() => togglePanel(PANELS.EXPORT)}
            />
            <SideControl title="Discard Changes" glyph={GLYPHS.CLOSE_CIRCLE} onClick={onMapReset} />
            {mapSaved && <SideControl title="Delete Map" glyph={GLYPHS.DELETE} onClick={onMapDelete} />}
            <SideControl
                toggle
                glyph={fullscreen ? GLYPHS.CHEVRON_RIGHT : GLYPHS.CHEVRON_LEFT}
                onClick={onFullScreenToggle}
            />
            <SideControl
                title="Help"
                glyph={GLYPHS.HELP}
                active={isPanelActive(PANELS.HELP)}
                onClick={() => togglePanel(PANELS.HELP)}
            />
        </StyledSideToolbar>
    );
};

SideToolbar.propTypes = propTypes;

const mapStateToProps = (state) => ({
    activePanel: getActivePanel(state),
    fullscreen: state.navigation.fullscreen,
    isAuthenticated: currentUserAuthenticatedSelector(state),
    hasApiKey: !!state.map.details.apiKey,
});

const mapDispatchToProps = (dispatch) => ({
    onPanelOpen: (panel, isAuthenticated, mapSaved, hasApiKey) => {
        if (panel === PANELS.SAVE || panel === PANELS.EXPORT) {
            if (!isAuthenticated) {
                dispatch(showModal(MODAL.NOT_AUTHENTICATED));
                return;
            }
        }
        if (panel === PANELS.EXPORT && (!mapSaved || !hasApiKey)) {
            dispatch(showModal(MODAL.CONFIRM_EXPORT_MAP));
            return;
        }
        dispatch(changePanel(panel));
    },
    onPanelClose: () => dispatch(closePanel()),
    onMapDelete: () => dispatch(showModal(MODAL.CONFIRM_MAP_DELETE)),
    onMapReset: () => dispatch(showModal(MODAL.CONFIRM_MAP_RESET)),
    onFullScreenToggle: () => dispatch(toggleFullscreen()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SideToolbar);
